import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  Box, Card, Stack, Switch, Link, Typography, Skeleton,
} from '@stvgroup/stv-ui-components'
import Avatar from '../Avatar'
import MenuArrowIcon from '../icons/MenuArrowIcon'
import getLoginPortalUrl from '../../utils/getLoginPortalUrl'
import cookies from '../../utils/cookies'
import { handleToggleSetting } from '../../utils/toggleSettings'
import { responsiveGlobalPadding } from '../../styles/layout'

const MenuItem = ({ children, isLoading }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    sx={{ p: '12px 24px', width: '100%' }}
  >
    {isLoading ? <Skeleton sx={{ width: '100%', height: '1.6em' }} /> : children}
  </Stack>
)

const MenuLink = ({
  href, label, onClick = () => {}, sx,
}) => (
  <Box
    sx={{
      cursor: 'pointer',
      textDecoration: 'none',
      width: '100%',
      display: 'block',
      ...sx,
    }}
  >
    <Link
      href={href}
      onClick={onClick}
      sx={{
        textDecoration: 'none',
        width: '100%',
        display: 'block',
        color: 'inherit',
        backgroundColor: 'red',
        '&:hover': {
          backgroundColor: '#313131',
          textDecoration: 'none',
        },
      }}
    >
      <MenuItem>
        <Typography>{label}</Typography>
        <MenuArrowIcon />
      </MenuItem>
    </Link>
  </Box>
)

const MenuSettingToggle = ({
  label, onToggle, defaultChecked = false, isLoading,
}) => (
  <MenuItem isLoading={isLoading}>
    <Typography>{label}</Typography>
    <Switch sx={{ '& .MuiSwitch-switchBase': { color: 'white !important' } }} onChange={onToggle} defaultChecked={defaultChecked} />
  </MenuItem>
)

const AccountSummary = ({
  firstName = '',
  currentlySubscribed = false,
  currentDomain,
  loginService,
  subscriptionType = '',
  userId,
}) => {
  const profileHost = getLoginPortalUrl(currentDomain)
  const isLoading = firstName === '' || !userId

  useEffect(() => {
    // Hide the element with class 'PageFooter'
    const pageFooter = document.querySelector('.PageFooter')
    if (pageFooter) {
      pageFooter.style.display = 'none'
    }
  }, [])

  const handleLogOutButtonClick = async () => {
    await loginService.logOut()
    window.location = `https://player.${currentDomain}`
  }

  return (
    <Box className="AccountSummary" sx={{ px: responsiveGlobalPadding }}>
      <Stack
        direction="column"
        alignItems="center"
        sx={{
          width: '100%',
        }}
      >
        <Typography sx={{
          margin: 'auto',
          mb: '12px',
          textTransform: 'capitalize',
          fontSize: '22px',
          fontWeight: 600,
          lineHeight: '27.65px',
          textAlign: 'center',
        }}
        >
          {isLoading
            ? <Skeleton variant="text" sx={{ width: '6em', height: '22px' }} />
            : `Hello ${firstName}`}
        </Typography>

        <Box sx={{
          width: { xs: '80px', sm: '95px', lg: '120px' },
          height: { xs: '80px', sm: '95px', lg: '120px' },
          '& span': {
            fontSize: { xs: '32px', sm: '42px', lg: '64px' },
          },
          borderRadius: '128px',
          boxShadow: isLoading ? 'none' : getBoxShadow(subscriptionType),
        }}
        >
          <Avatar userInitial={firstName?.charAt(0) || ''} summaryPage vipOptIn />
        </Box>

        <Stack sx={{ width: '100%', maxWidth: '376px', m: '16px 0 24px 0' }} spacing={1.5}>
          <Typography variant="h3">Manage Account</Typography>
          <Card sx={{ p: 0, mb: '8px' }} color="dark">
            <MenuLink href={`${profileHost}/personal-details`} label="Personal details" />
            <MenuLink href={`${profileHost}/change-password`} label="Change password" />
            <MenuLink href={`${profileHost}/marketing`} label="Marketing" />
            <MenuLink href={`${profileHost}/privacy/personalisation`} label="Privacy" />
            <MenuSettingToggle
              label="Guidance Warnings"
              onToggle={state => handleToggleSetting(
                'GUIDANCE_WARNINGS',
                state.target.checked,
                userId
              )}
              isLoading={isLoading}
              defaultChecked={cookies.get(`STV_${userId}_GUIDANCE_WARNINGS`) !== 'false'}
            />

            {/* <MenuSettingToggle
              label="Subtitles"
              onToggle={(state) => handleToggleSetting(
                'SUBTITLES',
                state.target.checked,
                userId
              )}
              defaultChecked={cookies.get(`STV_${userId}_SUBTITLES`) === 'true'}

            /> */}
          </Card>

          <Typography variant="h3">Subscription & billing</Typography>
          <Card sx={{ p: 0, mt: 0 }} color="dark">
            <MenuLink href={`${profileHost}/plan`} label="Manage subscription" />
            {currentlySubscribed && <MenuLink href={`${profileHost}/payment-details`} label="Payment details" />}
          </Card>

          <Card sx={{ p: 0 }} color="dark">
            <MenuLink
              sx={{
                '& .MuiTypography-root': {
                  color: '#E16A67',
                },
              }}
              onClick={handleLogOutButtonClick}
              label="Sign out"
            />
          </Card>
        </Stack>
      </Stack>
    </Box>
  )
}

MenuItem.propTypes = {
  children: PropTypes.node.isRequired,
  isLoading: PropTypes.bool,
}

MenuLink.propTypes = {
  href: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  sx: PropTypes.object,
}

MenuSettingToggle.propTypes = {
  label: PropTypes.string.isRequired,
  onToggle: PropTypes.func.isRequired,
  defaultChecked: PropTypes.bool,
  isLoading: PropTypes.bool,
}

AccountSummary.defaultProps = {
  firstName: '',
  currentlySubscribed: false,
  subscriptionType: '',
}

AccountSummary.propTypes = {
  firstName: PropTypes.string,
  currentlySubscribed: PropTypes.bool,
  currentDomain: PropTypes.string.isRequired,
  loginService: PropTypes.shape({
    logOut: PropTypes.func,
    deleteUser: PropTypes.func,
    getUser: PropTypes.func,
  }).isRequired,
  subscriptionType: PropTypes.string,
  userId: PropTypes.string.isRequired,
}

function getBoxShadow(subscriptionType) {
  switch (subscriptionType) {
    case 'plus':
      return '#549d86 0 0 110px 1px'
    case 'premier_sports':
      return '#ffff00 0 0 110px 1px'
    default:
      return 'rgb(131 64 223) 0px 0px 110px 1px'
  }
}

export default AccountSummary
